import { lazy } from "react";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import {
  ROUTES,
  updateRoutesBusy,
  updateRoutesError,
  updateRoutesSuccess,
  LOAD,
  doLoadBusy,
  doLoadError,
  doLoadSuccess,
  DEFAULT_MODELS,
  buildDefaultModelsBusy,
  buildDefaultModelsError,
  buildDefaultModelsSuccess,
} from "./action";
import { ActionTypes } from "../util";
import { logError } from "utils/utils";
import { routes } from "routes";
import { buildTree } from "utils/tree";
import { concat } from "lodash";
import { readServicesSaga } from "controllers/services/saga";
import { buildModel } from "components/Template";
import { FETCH_SERVICES, selectServices } from "controllers/services/action";
import dashboardProductsRoute from "routes/Dashboard/Products/route";
import homeProductsRoute from "routes/Info/OurProducts/route";
import { readUserSaga } from "controllers/user/saga";
import { readSamplesSaga } from "controllers/samples/saga";

const Product = lazy(() => import("routes/Dashboard/Products/Product"));
const ProductGuide = lazy(() => import("routes/Info/OurProducts/ProductGuide"));

const { REQUEST, SUCCESS } = ActionTypes;

function createRoutes(result) {
  const base = Object.values(routes.map).map((r) => r.data);
  const dashboardProducts = result?.map((r, i) => ({
    id: dashboardProductsRoute.id * 1000 + i,
    parentId: dashboardProductsRoute.id,
    name: r.name,
    label: r.label,
    description: r.description,
    brief: r.brief,
    feature: r.feature,
    path: `${r.name}`,
    element: Product,
    icon: r.icon,
    user: true,
    admin: false,
    hidden: false,
    buildProps: () => ({ template: r, model: buildModel(r) }),
  }));
  const homeProducts = result?.map((r, i) => ({
    id: homeProductsRoute.id * 1000 + i,
    parentId: homeProductsRoute.id,
    name: r.name,
    label: r.label,
    description: r.description,
    brief: r.brief,
    keywords: r.keywords,
    path: `${r.name}`,
    element: ProductGuide,
    footer: false,
    icon: r.icon,
    user: true,
    admin: false,
    hidden: false,
    buildProps: () => ({ url: r.markdown }),
  }));
  // todo: add other dynamic routes here and insert into concat below
  // const infoProducts = result?map...
  const all = concat(base, dashboardProducts, homeProducts)
    .filter((v) => v)
    .sort((a, b) => a.id - b.id);
  return buildTree(all);
}

export function* updateRoutesSaga(action) {
  const { payload } = action;
  try {
    yield put(updateRoutesBusy(true));
    yield put(updateRoutesError());
    const result = yield call(createRoutes, payload);
    yield put(updateRoutesSuccess(result));
  } catch (error) {
    logError(error);
    yield put(updateRoutesError(error.message));
  } finally {
    yield put(updateRoutesBusy(false));
  }
}

// define sagas to load on application start here
const doLoadSagas = [readUserSaga, readServicesSaga, readSamplesSaga, buildDefaultModelsSaga];

export function* doLoadSaga() {
  try {
    yield put(doLoadBusy(true));
    yield put(doLoadError());
    for (const saga of doLoadSagas) {
      yield call(saga);
    }
    yield put(doLoadSuccess(true));
  } catch (error) {
    logError(error);
    yield put(doLoadError(error.message));
  } finally {
    yield put(doLoadBusy(false));
  }
}

export function* buildDefaultModelsSaga() {
  try {
    const services = yield select(selectServices);
    yield put(buildDefaultModelsBusy(true));
    yield put(buildDefaultModelsError());
    let result = {};
    for (const service of services) {
      const model = yield call(buildModel, service);
      result[service.name] = model;
    }
    yield put(buildDefaultModelsSuccess(result));
  } catch (error) {
    logError(error);
    yield put(buildDefaultModelsError(error.message));
  } finally {
    yield put(buildDefaultModelsBusy(false));
  }
}

export default function* commonSaga() {
  yield takeLatest(LOAD[REQUEST], doLoadSaga);
  yield takeEvery(ROUTES[REQUEST], updateRoutesSaga);
  yield takeLatest(FETCH_SERVICES[SUCCESS], updateRoutesSaga);
  yield takeLatest(DEFAULT_MODELS[REQUEST], buildDefaultModelsSaga);
  yield takeLatest(FETCH_SERVICES[SUCCESS], buildDefaultModelsSaga);
}
